//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { FiniteDuration } from '@bvengineering/universal-scheduling-gateway-service-types';
import { PojoSet } from 'pojo-sets';

import { ScheduleInstagramPostErrorCode } from '@apis/publish/publishApiModels';
import { TPostConnection } from '@apis/universal-posts/connections/universalPostConnectionModel';
import { IInstagramPostDetailsDefaultsState } from '@instagram-account/redux/post-details/defaults/instagramPostDetailsDefaultsModel';
import { IInstagramPostDetailsMetadataState } from '@instagram-account/redux/post-details/metadata/instagramPostDetailsMetadataModel';
import { IInstagramPostDetailsModalState } from '@instagram-account/redux/post-details/modal/instagramPostDetailsModalModel';
import { IShowroomData } from '@legacy-components/common/universal-image-actions/models/events';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import { IInstagramApiLocation } from '@models/IInstagramLocation';
import { IShopTag } from '@models/IShopTag';
import { ITaggedUser } from '@models/ITaggedUser';
import InstagramHashtag from '@models/InstagramHashtag';
import { InstagramPostStatus } from '@models/InstagramPostStatus';
import { InstagramPostType } from '@models/InstagramPostType';
import { InstagramPublishingStrategy } from '@models/InstagramPublishingStrategy';
import PostableId from '@models/PostableId';
import { ScheduledItemFailureStatus } from '@models/ScheduledItemFailureStatus';
import { UniversalPostableCustomStatus } from '@models/UniversalPostableCustomStatus';
import UserId from '@models/UserId';
import ScheduledInstagramPostMetadataId from '@models/tiny-types/ScheduledInstagramPostMetadataId';

import PartialCuid from 'app/models/PartialCuid';
import { RoutableStateName } from 'app/top-level-states';

export enum INSTAGRAM_POST_DETAILS_ACTIONS {
  SET_INITIAL_POST_DETAILS = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_INITIAL_POST_DETAILS',

  SET_SCHEDULING_ENTRY_POINT = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_SCHEDULING_ENTRY_POINT',

  SET_ELIGIBLE_USERS = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_ELIGIBLE_USERS',
  SET_PREVIEW_CUID_INDEX = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_PREVIEW_CUID_INDEX',

  REQUEST_SELECTED_MEDIA = 'INSTAGRAM_POST_DETAILS/REQUEST_SELECTED_MEDIA',
  RECEIVE_SELECTED_MEDIA_SUCCEEDED = 'INSTAGRAM_POST_DETAILS/RECEIVE_SELECTED_MEDIA_SUCCEEDED',
  RECEIVE_SELECTED_MEDIA_FAILED = 'INSTAGRAM_POST_DETAILS/RECEIVE_SELECTED_MEDIA_FAILED',
  REMOVE_SELECTED_CUID = 'INSTAGRAM_POST_DETAILS_ACTIONS/REMOVE_SELECTED_CUID',
  UPDATE_CUID_POSITION = 'INSTAGRAM_POST_DETAILS_ACTIONS/UPDATE_CUID_POSITION',

  SET_VIDEO_THUMBNAIL_OFFSET = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_VIDEO_THUMBNAIL_OFFSET',
  REMOVE_VIDEO_THUMBNAIL_OFFSET = 'INSTAGRAM_POST_DETAILS_ACTIONS/REMOVE_VIDEO_THUMBNAIL_OFFSET',
  SET_CAPTION = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_CAPTION',
  SET_FIRST_COMMENT = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_FIRST_COMMENT',
  CLEAR_FIRST_COMMENT = 'INSTAGRAM_POST_DETAILS_ACTIONS/CLEAR_FIRST_COMMENT',
  ADD_TAGGED_USER = 'INSTAGRAM_POST_DETAILS_ACTIONS/ADD_TAGGED_USER',
  REMOVE_TAGGED_USER = 'INSTAGRAM_POST_DETAILS_ACTIONS/REMOVE_TAGGED_USER',
  UPDATE_TAGGED_USER = 'INSTAGRAM_POST_DETAILS_ACTIONS/UPDATE_TAGGED_USER',
  REMOVE_ALL_TAGGED_USERS = 'INSTAGRAM_POST_DETAILS_ACTIONS/REMOVE_ALL_TAGGED_USERS',
  ADD_TAGGED_PRODUCT = 'INSTAGRAM_POST_DETAILS_ACTIONS/ADD_TAGGED_PRODUCT',
  REMOVE_TAGGED_PRODUCT = 'INSTAGRAM_POST_DETAILS_ACTIONS/REMOVE_TAGGED_PRODUCT',
  UPDATE_TAGGED_PRODUCT = 'INSTAGRAM_POST_DETAILS_ACTIONS/UPDATE_TAGGED_PRODUCT',
  REMOVE_ALL_TAGGED_PRODUCTS = 'INSTAGRAM_POST_DETAILS_ACTIONS/REMOVE_ALL_TAGGED_PRODUCTS',
  SET_TAGGED_LOCATION = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_TAGGED_LOCATION',
  REMOVE_TAGGED_LOCATION = 'INSTAGRAM_POST_DETAILS_ACTIONS/REMOVE_TAGGED_LOCATION',
  SET_POST_TIME = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_POST_TIME',
  SET_NOTIFICATION_USER = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_NOTIFICATION_USER',
  SET_IS_FIRST_COMMENT_FIELD_EXPANDED = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_IS_FIRST_COMMENT_FIELD_EXPANDED',

  SET_PUBLISHING_STRATEGY = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_PUBLISHING_STRATEGY',
  SET_SHOULD_CREATE_FB_CROSS_POST = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_SHOULD_CREATE_FB_CROSS_POST',
  SET_SHOULD_CREATE_TW_CROSS_POST = 'INSTAGRAM_POST_DETAILS_ACTIONS/SET_SHOULD_CREATE_TW_CROSS_POST',

  SET_VALIDATION_ERRORS = 'INSTAGRAM_POST_DETAILS/SET_VALIDATION_ERRORS',
  SET_INVALID_CUIDS = 'INSTAGRAM_POST_DETAILS/SET_INVALID_CUIDS',

  REQUEST_SCHEDULE_POST = 'INSTAGRAM_POST_DETAILS/REQUEST_SCHEDULE_POST',
  RECEIVE_SCHEDULE_POST_SUCCEEDED = 'INSTAGRAM_POST_DETAILS/RECEIVE_SCHEDULE_POST_SUCCEEDED',
  RECEIVE_SCHEDULE_POST_FAILED = 'INSTAGRAM_POST_DETAILS/RECEIVE_SCHEDULE_POST_FAILED',

  REQUEST_DELETE_POST = 'INSTAGRAM_POST_DETAILS/REQUEST_DELETE_POST',
  RECEIVE_DELETE_POST_SUCCEEDED = 'INSTAGRAM_POST_DETAILS/RECEIVE_DELETE_POST_SUCCEEDED',
  RECEIVE_DELETE_POST_FAILED = 'INSTAGRAM_POST_DETAILS/RECEIVE_DELETE_POST_FAILED',

  RESET_POST_DETAILS = 'INSTAGRAM_POST_DETAILS_ACTIONS/RESET_POST_DETAILS',

  REQUEST_TOP_HASHTAG_RECOMMENDATIONS = 'INSTAGRAM_POST_DETAILS_ACTIONS/REQUEST_TOP_HASHTAG_RECOMMENDATIONS',
  RECEIVE_TOP_HASHTAG_RECOMMENDATIONS_SUCCEEDED = 'INSTAGRAM_POST_DETAILS_ACTIONS/RECEIVE_TOP_HASHTAG_RECOMMENDATIONS_SUCCEEDED',
  RECEIVE_TOP_HASHTAG_RECOMMENDATIONS_FAILED = 'INSTAGRAM_POST_DETAILS_ACTIONS/RECEIVE_TOP_HASHTAG_RECOMMENDATIONS_FAILED',
}

export interface ICombinedInstagramPostDetailsState
  extends IInstagramPostDetailsState {
  defaults: IInstagramPostDetailsDefaultsState;
  metadata: IInstagramPostDetailsMetadataState;
  modals: IInstagramPostDetailsModalState;
}

/**
 * Pieces of information about the current post that cannot be altered
 * by any user input in the post-details form. Mostly saved for the purpose
 * of passing into the edit post endpoints.
 */
export interface IStaticInstagramPostDetails {
  // Indicates a failure at post time, used to show error UI at the top of the post details form fields
  lastFailureStatus?: ScheduledItemFailureStatus;
  postId?: PostableId;
  draftId?: DraftId;
  postMetadataId?: ScheduledInstagramPostMetadataId;
  postStatus?: InstagramPostStatus;
  postType: InstagramPostType;
  userActionStatus?: UniversalPostableCustomStatus;
  connections?: TPostConnection[];
}

export type InstagramPostDetailsField = keyof IComparableInstagramPostDetails;
/**
 * Pieces of information about the current post that can be directly
 * altered by user input in the post details form.
 */
export interface IComparableInstagramPostDetails {
  caption?: string;
  firstComment?: string;
  notificationUserId?: UserId;
  postTime?: Date;
  publishingStrategy: InstagramPublishingStrategy;
  shouldCreateFbCrossPost: boolean;
  shouldCreateTwCrossPost: boolean;
  selectedCuids: Cuid[];
  taggedLocation?: IInstagramApiLocation;
  taggedUsers?: ITaggedUser[];
  taggedProducts?: IShopTag[];
  videoThumbnailOffsetSeconds?: number;
}

export interface IPostDetailsShowroomData {
  data: IShowroomData;
  shortLink: string;
}

export interface IInstagramPostDetailsState
  extends IStaticInstagramPostDetails {
  currentPostDetails: IComparableInstagramPostDetails;
  errors: Partial<IInstagramPostDetailsErrors>;
  flags: IInstagramPostDetailsFlags;
  hashtagRecommendations: InstagramHashtag[];
  initialPostDetails?: IComparableInstagramPostDetails;
  previewCuidIndex: number;
  schedulingEntryPoint?: RoutableStateName;
  shouldCreateFbCrossPost: boolean;
  shouldCreateTwCrossPost: boolean;
  validationErrors: PojoSet<InstagramPostDetailsValidationError>;
  invalidCuids: PojoSet<Cuid>;
  isFirstCommentFieldExpanded: boolean;
}

interface IInstagramPostDetailsErrors {
  setInitialPostDetailsError: string;
  fetchTopHashtagRecommendationsError: string;
  fetchSelectedMediaError: string;
  fetchAugmentedLinkError?: string;
  // Indicates a failure at schedule time, used to show error UI at the bottom of the post details form fields
  scheduleFailureCode?: ScheduleInstagramPostErrorCode;
  scheduleFailureError?: string;
}

interface IInstagramPostDetailsFlags {
  hasSetPostDetails: boolean;
  isFetchingTopHashtagRecommendations: boolean;
  isFetchingSelectedMedia: boolean;
  isSchedulingPost: boolean;
  isDeletingPost: boolean;
}

export enum InstagramPostDetailsValidationError {
  Gif = 'Gif',
  InvalidMediaAspectRatio = 'InvalidMediaAspectRatio',
  InvalidUrl = 'InvalidUrl',
  InvalidVideoLength = 'InvalidVideoLength',
  InvalidFeedVideoSize = 'InvalidFeedVideoSize',
  InvalidIgImageSize = 'InvalidIgImageSize',
  InvalidReelsVideoSize = 'InvalidReelsVideoSize',
  InvalidStoryVideo = 'InvalidStoryVideo',
  NoMedia = 'NoMedia',
  NoMediaStoryClip = 'NoMediaStoryClip',
  NoNotificationUser = 'NoNotificationUser',
  NoPostTime = 'NoPostTime',
  PostTimeInPast = 'PostTimeInPast',
  TooManyHashtags = 'TooManyHashtags',
  TooManyShopTags = 'TooManyShopTags',
  CaptionTooLong = 'CaptionTooLong',
  FirstCommentTooManyHashtags = 'FirstCommentTooManyHashtags',
  FirstCommentTooLong = 'FirstCommentTooLong',
}

export interface IMediaItem {
  cuid: PartialCuid;
}

export const MAX_IG_GALLERY_POST_MEDIA_COUNT = 10;
export const MAX_SHOP_TAG_COUNT = 5; // Limit is from the container creation endpoint: https://developers.facebook.com/docs/instagram-api/reference/ig-user/media#creating

// Setting a default limit of 30 hashtags
export const TOP_INSTAGRAM_HASHTAG_RECOMMENDATIONS_LIMIT = 30;

// The backend expects and returns the videoThumbailOffset in milliseconds
const VIDEO_OFFSET_CONVERSION_FACTOR = 1000;
export const videoThumbnailOffsetSecondsToFiniteDuration = (
  offsetSeconds?: number,
): FiniteDuration => ({
  length: (offsetSeconds ?? 0) * VIDEO_OFFSET_CONVERSION_FACTOR,
  unit: 'MILLISECONDS',
  finite: true,
});
export const videoThumbnailOffsetMsToSeconds = (offsetMs?: number) =>
  (offsetMs ?? 0) / VIDEO_OFFSET_CONVERSION_FACTOR;

export const IG_SCHEDULER_CONTENT_DRAWER_FILTERS = {
  initialFilters: {
    distribution: ['s:pinterest', 's:instagram'],
    isDistributed: ['false'],
  },
};

export const fallbackComparablePostDetails: IComparableInstagramPostDetails = {
  caption: '',
  publishingStrategy: InstagramPublishingStrategy.Automatic,
  selectedCuids: [],
  shouldCreateFbCrossPost: false,
  shouldCreateTwCrossPost: false,
};

export const fallbackStaticDetailsPostTypes: IStaticInstagramPostDetails = {
  postType: InstagramPostType.Post,
};
